import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Package, DollarSign, TrendingUp } from 'lucide-react';
import { PartnerService } from '../../services/partnerService';

const PartnerDashboard = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');

  const { data: ordersData, isLoading: ordersLoading } = useQuery({
    queryKey: ['partnerOrders', user?.id],
    queryFn: () => PartnerService.getOrders(user?.id),
    enabled: !!user?.id,
  });

  const { data: revenueData, isLoading: revenueLoading } = useQuery({
    queryKey: ['partnerRevenue', user?.id],
    queryFn: () => PartnerService.getRevenue(user?.id),
    enabled: !!user?.id,
  });

  const orders = ordersData?.data || [];
  const revenue = revenueData?.data || {};

  const stats = {
    activeOrders: orders.filter(o => o.status !== 'completed' && o.status !== 'cancelled').length,
    totalRevenue: revenue.totalRevenue || revenue.revenue || 0,
    completedOrders: orders.filter(o => o.status === 'completed').length,
    monthlyRevenue: revenue.monthlyRevenue || revenue.currentMonthRevenue || 0,
  };

  if (ordersLoading || revenueLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-slate-500 to-gray-600 rounded-xl p-6 text-white">
          <h1 className="text-3xl font-bold mb-2">Partner Dashboard</h1>
          <p className="text-white/80">Business partnership management</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="w-16 h-16 border-4 border-slate-500 border-t-transparent rounded-full spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-slate-500 to-gray-600 rounded-xl p-6 text-white">
        <h1 className="text-3xl font-bold mb-2">Partner Dashboard</h1>
        <p className="text-white/80">Business partnership management</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Active Orders</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeOrders}</p>
            </div>
            <Package size={24} className="text-slate-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">₹{Number(stats.totalRevenue).toLocaleString('en-IN')}</p>
            </div>
            <DollarSign size={24} className="text-green-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Completed Orders</p>
              <p className="text-2xl font-bold text-gray-900">{stats.completedOrders}</p>
            </div>
            <TrendingUp size={24} className="text-blue-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Monthly Revenue</p>
              <p className="text-2xl font-bold text-gray-900">₹{Number(stats.monthlyRevenue).toLocaleString('en-IN')}</p>
            </div>
            <DollarSign size={24} className="text-purple-600" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button 
            onClick={() => navigate('/partner/orders')}
            className="px-4 py-3 bg-slate-500 text-white rounded-lg hover:bg-slate-600"
          >
            View Orders
          </button>
          <button 
            onClick={() => navigate('/partner/revenue')}
            className="px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            View Revenue
          </button>
        </div>
      </div>
    </div>
  );
};

export default PartnerDashboard;

